import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { EMPTY_DEAL } from "@/components/SoldDealDialog";

const DealRow = ({ d, i, count, onEdit, onChange }) => {
  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggle = (visible) => act(() => api.put(`/admin/sold-deals/${d.deal_id}`, { ...EMPTY_DEAL, ...d, visible }), visible ? "Cession visible" : "Cession masquée");
  const move = (direction) => act(() => api.post(`/admin/sold-deals/${d.deal_id}/move`, { direction }));
  const remove = () => window.confirm(`Supprimer définitivement « ${d.name} » ?`) && act(() => api.delete(`/admin/sold-deals/${d.deal_id}`), "Cession supprimée");
  return (
    <tr data-testid={`deal-row-${d.deal_id}`} className={`border-t border-gray-100 ${d.visible === false ? "opacity-60" : ""}`}>
      <td className="p-3 whitespace-nowrap">
        <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move("up")} data-testid={`deal-up-${d.deal_id}`} aria-label="Monter" className="h-8 w-8"><ArrowUp className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" disabled={i === count - 1} onClick={() => move("down")} data-testid={`deal-down-${d.deal_id}`} aria-label="Descendre" className="h-8 w-8"><ArrowDown className="w-4 h-4" /></Button>
      </td>
      <td className="p-3"><div className="w-20 aspect-[3/2] bg-[#F8F6F0] overflow-hidden">{d.image && <img src={d.image} alt="" className="w-full h-full object-cover" />}</div></td>
      <td className="p-3 min-w-[220px]">
        <div className="font-medium text-[#1B2A4A]">{d.reference && <span className="mr-2 text-xs text-[#94A3B8]">#{d.reference}</span>}{d.name}</div>
        <div className="text-xs text-[#475569] mt-0.5">{d.sector}{d.buyer && ` · Repreneur : ${d.buyer}`}</div>
      </td>
      <td className="p-3 text-[#475569] whitespace-nowrap">{d.region}{d.department && ` (${d.department})`}</td>
      <td className="p-3"><div className="flex items-center gap-2"><Switch data-testid={`deal-visible-${d.deal_id}`} checked={d.visible !== false} onCheckedChange={toggle} /><span className="text-xs text-[#475569]">{d.visible === false ? "Masquée" : "Visible"}</span></div></td>
      <td className="p-3 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={() => onEdit(d)} data-testid={`deal-edit-${d.deal_id}`} className="rounded-none"><Pencil className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" onClick={remove} data-testid={`deal-delete-${d.deal_id}`} className="rounded-none text-red-600"><Trash2 className="w-4 h-4" /></Button>
      </td>
    </tr>
  );
};

export default function SoldDealsPanel({ version, onEdit }) {
  const [deals, setDeals] = useState([]);
  const load = () => api.get("/admin/sold-deals").then((r) => setDeals(r.data)).catch(() => {});
  useEffect(() => { load(); }, [version]);
  const visible = deals.filter((d) => d.visible !== false).length;

  return (
    <div className="space-y-4" data-testid="sold-deals-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#475569]" data-testid="sold-deals-count">{deals.length} cession(s) · {visible} visible(s) sur le site</p>
        <div className="flex flex-wrap gap-2">
          <a href="/entreprises-vendues" target="_blank" rel="noreferrer" data-testid="sold-deals-public-link" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 border border-gray-300 rounded-full hover:border-[#1B2A4A] transition-colors"><ExternalLink className="w-4 h-4" /> Voir la page publique</a>
          <Button data-testid="sold-deal-add" onClick={() => onEdit({ ...EMPTY_DEAL })} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold"><Plus className="w-4 h-4 mr-1" /> Ajouter une cession</Button>
        </div>
      </div>
      <div className="bg-white border border-gray-200 overflow-x-auto">
        {deals.length === 0 ? <p className="text-gray-400 py-10 text-center text-sm">Aucune cession enregistrée.</p> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-[#475569]">
              <tr><th className="p-3">Ordre</th><th className="p-3">Image</th><th className="p-3">Entreprise</th><th className="p-3">Localisation</th><th className="p-3">Site public</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>{deals.map((d, i) => <DealRow key={d.deal_id} d={d} i={i} count={deals.length} onEdit={onEdit} onChange={load} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
